import {
  Button,
  InputErrorMessage,
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@commenda/kakashi';
import { useRef, useState } from 'react';

import type { MonthKey } from '../types/period';
import { MonthSelect } from './PeriodControls';

interface ICustomRangePopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Selects are prefixed with this so ids stay unique across cards. */
  idPrefix: string;
  initialFrom: MonthKey;
  initialTo: MonthKey;
  months: MonthKey[];
  booksClosedThrough: MonthKey;
  onApply: (range: { from: MonthKey; to: MonthKey }) => void;
  children: React.ReactNode;
}

const RANGE_ERROR = 'From must be the same month as To or earlier';

type ICustomRangeFormProps = Pick<
  ICustomRangePopoverProps,
  | 'idPrefix'
  | 'initialFrom'
  | 'initialTo'
  | 'months'
  | 'booksClosedThrough'
  | 'onApply'
>;

const CustomRangeForm = ({
  idPrefix,
  initialFrom,
  initialTo,
  months,
  booksClosedThrough,
  onApply,
}: ICustomRangeFormProps) => {
  const [from, setFrom] = useState(initialFrom);
  const [to, setTo] = useState(initialTo);
  const isInvalid = from > to;

  return (
    <div className="flex flex-col gap-lg">
      <div className="flex gap-md">
        <MonthSelect
          id={`${idPrefix}-from`}
          label="From"
          toolbar={false}
          value={from}
          onChange={setFrom}
          months={months}
          booksClosedThrough={booksClosedThrough}
        />
        <MonthSelect
          id={`${idPrefix}-to`}
          label="To"
          toolbar={false}
          value={to}
          onChange={setTo}
          months={months}
          booksClosedThrough={booksClosedThrough}
        />
      </div>
      {isInvalid && <InputErrorMessage message={RANGE_ERROR} />}
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={isInvalid}
          onClick={() => onApply({ from, to })}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

/**
 * Card-anchored From/To month picker; Esc or clicking outside cancels.
 * Focus returning to the card menu trigger when the menu closes must not dismiss it.
 */
export const CustomRangePopover = ({
  open,
  onOpenChange,
  children,
  ...form
}: ICustomRangePopoverProps) => {
  const anchorRef = useRef<HTMLDivElement>(null);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor ref={anchorRef} asChild>
        {children}
      </PopoverAnchor>
      {open && (
        <PopoverContent
          align="start"
          className="w-auto p-lg"
          data-no-drag
          onOpenAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => {
            if (
              e.target instanceof Node &&
              anchorRef.current?.contains(e.target)
            ) {
              e.preventDefault();
            }
          }}
        >
          <CustomRangeForm {...form} />
        </PopoverContent>
      )}
    </Popover>
  );
};
